import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Award, Target, Eye, Heart, Users, Globe, TrendingUp, Shield, Clock, CheckCircle, Briefcase, GraduationCap } from 'lucide-react';
interface AboutUsProps {
  onBack?: () => void;
}
const milestones = [{
  year: '2003',
  title: 'Company Founded',
  description: 'Started our journey in Muscat, Oman'
}, {
  year: '2010',
  title: 'Regional Expansion',
  description: 'Expanded services across the GCC region'
}, {
  year: '2015',
  title: 'ISO Certification',
  description: 'Achieved ISO 27001 certification'
}, {
  year: '2020',
  title: 'Cloud Excellence',
  description: 'Became official AWS & Azure partner'
}, {
  year: '2024',
  title: 'Industry Leader',
  description: '50+ major enterprise clients served'
}];
const values = [{
  icon: Shield,
  title: 'Security First',
  titleAr: 'الأمان أولاً',
  description: 'We prioritize the security and confidentiality of our clients\' data',
  color: '#006847'
}, {
  icon: Target,
  title: 'Excellence',
  titleAr: 'التميز',
  description: 'Committed to delivering exceptional IT solutions and services',
  color: '#C8102E'
}, {
  icon: Heart,
  title: 'Integrity',
  titleAr: 'النزاهة',
  description: 'Building trust through transparency and ethical practices',
  color: '#D4AF37'
}, {
  icon: Users,
  title: 'Collaboration',
  titleAr: 'التعاون',
  description: 'Working together with clients to achieve their goals',
  color: '#3498DB'
}];
const stats = [{
  number: '20+',
  label: 'Years Experience',
  labelAr: 'سنة خبرة'
}, {
  number: '50+',
  label: 'IT Specialists',
  labelAr: 'متخصص تقنية'
}, {
  number: '200+',
  label: 'Projects Completed',
  labelAr: 'مشروع منجز'
}, {
  number: '98%',
  label: 'Client Satisfaction',
  labelAr: 'رضا العملاء'
}];
const certifications = ['ISO 27001:2013', 'AWS Partner Network', 'Microsoft Azure Partner', 'ITIL Foundation', 'CISSP Certified Team', 'PCI DSS Compliance'];
export default function AboutUs({
  onBack
}: AboutUsProps) {
  return <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#006847] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-[#006847] to-[#004d35] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              About Bareeq Tech
            </h1>
            <p className="text-xl text-white/90 mb-6">
              من نحن | نبذة عنا
            </p>
            <p className="text-lg text-white/80">
              Leading IT consulting and cybersecurity firm in the Sultanate of Oman, 
              empowering businesses with innovative technology solutions since 2003.
            </p>
          </motion.div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48h1440V0c-240 48-480 48-720 24C480 0 240 0 0 24v24z" fill="#F9FAFB" />
          </svg>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#006847] mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-700 font-medium">{stat.label}</div>
                <div className="text-sm text-gray-500">{stat.labelAr}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-16 h-16 bg-[#006847]/10 rounded-full flex items-center justify-center mb-6">
                <Target className="text-[#006847]" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
              <p className="text-lg text-gray-600 mb-4">
                To empower organizations in Oman and the GCC region with innovative IT solutions 
                that drive digital transformation and ensure cybersecurity excellence.
              </p>
              <p className="text-gray-600">
                We strive to be the trusted technology partner for businesses seeking to leverage 
                modern IT infrastructure, cloud computing, and advanced security solutions.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-sm">
              <div className="w-16 h-16 bg-[#C8102E]/10 rounded-full flex items-center justify-center mb-6">
                <Eye className="text-[#C8102E]" size={32} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
              <p className="text-lg text-gray-600 mb-4">
                To be the leading IT consulting firm in the region, recognized for innovation, 
                security excellence, and transformative technology solutions.
              </p>
              <p className="text-gray-600">
                We envision a future where every organization in our region has access to 
                world-class IT services that enable growth, efficiency, and competitive advantage.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              The principles that guide our work and relationships with clients
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{
              backgroundColor: `${value.color}20`
            }}>
                  <value.icon style={{
                color: value.color
              }} size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-gray-500 mb-3">{value.titleAr}</p>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Our Journey
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Two decades of innovation and excellence in IT services
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#006847]/20" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: index % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className={`flex items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className="bg-white p-6 rounded-2xl shadow-sm">
                      <div className="text-2xl font-bold text-[#006847] mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>

                  <div className="hidden md:block w-6 h-6 bg-[#006847] rounded-full border-4 border-white shadow-lg z-10" />

                  <div className="flex-1" />
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Certifications & Partnerships
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Recognized for our expertise and commitment to excellence
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {certifications.map((cert, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05
          }} className="bg-gray-50 p-6 rounded-xl text-center hover:shadow-lg transition-shadow">
                <Award className="text-[#D4AF37] mx-auto mb-3" size={32} />
                <p className="font-medium text-gray-700 text-sm">{cert}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-r from-[#006847] to-[#004d35] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Us
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              What sets us apart in the competitive IT services landscape
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[{
            icon: Globe,
            title: 'Regional Expertise',
            description: 'Deep understanding of the GCC market and local business requirements'
          }, {
            icon: Clock,
            title: '24/7 Support',
            description: 'Round-the-clock technical support and monitoring services'
          }, {
            icon: GraduationCap,
            title: 'Certified Professionals',
            description: 'Team of highly trained and certified IT specialists'
          }, {
            icon: TrendingUp,
            title: 'Proven Track Record',
            description: 'Successful delivery of 200+ projects for major enterprises'
          }, {
            icon: Shield,
            title: 'Security Focus',
            description: 'Comprehensive cybersecurity measures and compliance'
          }, {
            icon: Briefcase,
            title: 'Industry Solutions',
            description: 'Tailored services for various sectors and industries'
          }].map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl">
                <feature.icon className="text-[#D4AF37] mb-4" size={32} />
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-white/80">{feature.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
    </div>;
}